import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { CollapsiblePanel } from '@/core/components/CollapsiblePanel';
import { Icon } from '@/core/components/Icons';
import { useModal } from '@/core/hooks/useModal';
import { useAppDispatch } from '@/core/store/store';

import { getErrorMessage } from '@/utils/getErrorMessage';

import { ChatContent } from './components/ChatContent';
import { Header } from './components/Header';
import { Input } from './components/Input';
import { CloseModal } from './components/CloseModal/CloseModal';
import { SaveAnalysisModal } from './components/SaveAnalysisModal';
import {
  ChatBotWrapper,
  ChatContentWrapper,
  ChatContentContainer,
  InitialPromptArea,
  ScrollToBottomButton,
  ScrollToBottomWrapper,
} from './ChatBot.styles';
import {
  createAnalysis,
  fetchTokenUsage,
  getAnalysesList,
  tokenUsageSelector,
  tokenUsageLoadingSelector,
  tokenUsageErrorSelector,
} from './store';
import { useChatBot } from './hooks/useChatBot';
import { getCreateAnalysisPayload } from './utils/getCreateAnalysisPayload';
import { parseAnalysisContent } from './utils/parseAnalysisContent';
import { ChatBotProps } from './interfaces';

export const ChatBot = ({ focusId, onClose, onSaveSuccess }: ChatBotProps) => {
  const dispatch = useAppDispatch();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const tokenUsage = useSelector(tokenUsageSelector);
  const tokenUsageLoading = useSelector(tokenUsageLoadingSelector);
  const tokenUsageError = useSelector(tokenUsageErrorSelector);

  useEffect(() => {
    if (!tokenUsage && !tokenUsageLoading && !tokenUsageError) {
      dispatch(fetchTokenUsage());
    }
  }, [dispatch, tokenUsage, tokenUsageLoading, tokenUsageError]);
  const { isOpen: isCloseModalOpen, open: openCloseModal, close: closeCloseModal } = useModal();
  const wasStreamingRef = useRef(false);
  const { isOpen: isSaveModalOpen, open: openSaveModal, close: closeSaveModal } = useModal();
  const {
    messages,
    isStreaming,
    contentRef,
    autoScrollEnabled,
    hasMessages,
    canAttachFiles,
    handleChatScroll,
    scrollToBottom,
    handleSend,
    stopStream,
  } = useChatBot({ focusId });

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      dispatch(fetchTokenUsage());
    }
    wasStreamingRef.current = isStreaming;
  }, [dispatch, isStreaming]);

  const lastBotMessageText = useMemo(
    () => messages.filter(m => m.role === 'bot').pop()?.text ?? null,
    [messages]
  );
  const hasErrorInMessages = useMemo(() => messages.some(m => Boolean(m.error)), [messages]);
  const analysisTitle = lastBotMessageText?.trim()
    ? parseAnalysisContent(lastBotMessageText).title
    : '';

  const isSaveDisabled = useMemo(() => {
    return isStreaming || !lastBotMessageText || hasErrorInMessages;
  }, [isStreaming, lastBotMessageText, hasErrorInMessages]);

  const handleSaveConfirm = useCallback(async () => {
    if (focusId == null) {
      return;
    }

    try {
      const payload = getCreateAnalysisPayload(focusId, lastBotMessageText);

      unwrapResult(await dispatch(createAnalysis(payload)));

      dispatch(getAnalysesList());
      closeSaveModal();

      onSaveSuccess?.(focusId);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Failed to save analysis. Please try again.'));
    }
  }, [focusId, lastBotMessageText, closeSaveModal, dispatch, onSaveSuccess]);

  const hasUnsavedChanges = useMemo(
    () => messages.length > 0 || isStreaming,
    [messages.length, isStreaming]
  );

  const shouldCloseWithoutModal = useMemo(
    () => !lastBotMessageText || messages.length === 0 || hasErrorInMessages,
    [lastBotMessageText, messages.length, hasErrorInMessages]
  );

  return (
    <ChatBotWrapper>
      {hasMessages ? (
        <CollapsiblePanel
          isOpen={isSidePanelOpen}
          setIsOpen={setIsSidePanelOpen}
        />
      ) : null}
      <ChatContentWrapper $isSidePanelOpen={hasMessages && isSidePanelOpen}>
        <Header
          onClose={() => {
            if (shouldCloseWithoutModal || !hasUnsavedChanges) {
              onClose?.();
            } else {
              openCloseModal();
            }
          }}
          onSaveClick={openSaveModal}
          isSaveDisabled={isSaveDisabled}
        />
        {hasMessages ? (
          <>
            <ChatContentContainer>
              <ChatContent
                messages={messages}
                contentRef={contentRef}
                onScroll={handleChatScroll}
              />
              {!autoScrollEnabled ? (
                <ScrollToBottomWrapper>
                  <ScrollToBottomButton
                    type="button"
                    onClick={scrollToBottom}
                  >
                    <Icon
                      name="ChevronDown"
                      size={16}
                    />
                  </ScrollToBottomButton>
                </ScrollToBottomWrapper>
              ) : null}
            </ChatContentContainer>
            <Input
              onSend={handleSend}
              onStop={stopStream}
              isGenerating={isStreaming}
              canAttachFiles={canAttachFiles}
              placeholder="Type your message..."
            />
          </>
        ) : (
          <InitialPromptArea>
            <Input
              onSend={handleSend}
              onStop={stopStream}
              isGenerating={isStreaming}
              canAttachFiles={canAttachFiles}
              isCentered
              placeholder="Describe what you want analysed..."
            />
          </InitialPromptArea>
        )}
        <CloseModal
          isOpen={isCloseModalOpen}
          analysisTitle={analysisTitle}
          onCancel={closeCloseModal}
          onConfirm={() => {
            stopStream();
            closeCloseModal();
            onClose?.();
          }}
        />
        <SaveAnalysisModal
          isOpen={isSaveModalOpen}
          analysisTitle={analysisTitle}
          onCancel={closeSaveModal}
          onConfirm={handleSaveConfirm}
        />
      </ChatContentWrapper>
    </ChatBotWrapper>
  );
};
